// src/services/contentService.js
// Simple content service that loads from local JSON files

let contentCache = null;
let isLoaded = false;

/**
 * Load all content from JSON files
 */
async function loadContent() {
  if (isLoaded && contentCache) return contentCache;

  try {
    const [blogResponse, youtubeResponse, externalLinksResponse] = await Promise.all([
      fetch('/content/blog/blog.json'),
      fetch('/content/youtube/youtube.json'),
      fetch('/content/external_links/external_links.json')
    ]);

    const [blogs, youtube, externalLinks] = await Promise.all([
      blogResponse.json(),
      youtubeResponse.json(),
      externalLinksResponse.json()
    ]);

    // Combine all content with unique IDs (prefix to avoid collisions)
    contentCache = {
      blogs: blogs.map(b => ({ ...b, _type: 'blog' })),
      youtube: youtube.map(y => ({ ...y, _type: 'youtube' })),
      externalLinks: externalLinks.map(e => ({ ...e, _type: 'external_link' })),
      all: []
    };

    // Create combined array with prefixed IDs for uniqueness
    contentCache.all = [
      ...contentCache.blogs.map(b => ({ ...b, _uniqueId: `blog_${b.id}` })),
      ...contentCache.youtube.map(y => ({ ...y, _uniqueId: `youtube_${y.id}` })),
      ...contentCache.externalLinks.map(e => ({ ...e, _uniqueId: `external_${e.id}` }))
    ];

    isLoaded = true;
    console.log('Content loaded:', {
      blogs: contentCache.blogs.length,
      youtube: contentCache.youtube.length,
      externalLinks: contentCache.externalLinks.length
    });

    return contentCache;
  } catch (error) {
    console.error('Failed to load content:', error);
    throw error;
  }
}

/**
 * Fetch markdown content from a file path
 * @param {string} path - Path to the markdown file
 * @returns {Promise<string|null>} Markdown content or null
 */
async function fetchMarkdownContent(path) {
  if (!path) return null;

  try {
    const response = await fetch(path);
    if (!response.ok) {
      console.warn(`Failed to fetch markdown from ${path}: ${response.status}`);
      return null;
    }
    return await response.text();
  } catch (error) {
    console.error(`Error fetching markdown from ${path}:`, error);
    return null;
  }
}

/**
 * Get all published content ordered by updated_at DESC
 * @returns {Promise<Array>} Array of content items
 */
export async function getPublishedContent() {
  const content = await loadContent();

  // Combine all content and sort by updated_at DESC
  const allContent = content.all
    .filter(item => item.status === 'published')
    .sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at);
      const dateB = new Date(b.updated_at || b.created_at);
      return dateB - dateA;
    });

  return allContent;
}

/**
 * Get content by ID and type
 * @param {string} type - Content type (blog, youtube, external_link)
 * @param {number|string} id - Content ID
 * @returns {Promise<Object|null>} Content item or null
 */
export async function getContentByTypeAndId(type, id) {
  const content = await loadContent();
  const numId = parseInt(id, 10);

  let item = null;

  switch (type) {
    case 'blog':
      item = content.blogs.find(b => b.id === numId);
      break;
    case 'youtube':
      item = content.youtube.find(y => y.id === numId);
      break;
    case 'external_link':
      item = content.externalLinks.find(e => e.id === numId);
      break;
    default:
      return null;
  }

  if (!item || item.status !== 'published') return null;

  // If text_content_path is provided, fetch the markdown content
  if (item.text_content_path) {
    const markdownContent = await fetchMarkdownContent(item.text_content_path);
    if (markdownContent) {
      item = { ...item, text_content: markdownContent };
    }
  }

  return item;
}

/**
 * Get content by unique ID (format: type_id, e.g., "blog_1", "youtube_2")
 * @param {string} uniqueId - Unique content ID
 * @returns {Promise<Object|null>} Content item or null
 */
export async function getContentByUniqueId(uniqueId) {
  const [type, id] = uniqueId.split('_');
  return getContentByTypeAndId(type, id);
}

/**
 * Get blog content by ID
 * @param {number|string} id - Blog ID
 * @returns {Promise<Object|null>} Blog item or null
 */
export async function getBlogById(id) {
  return getContentByTypeAndId('blog', id);
}

/**
 * Get published content by type
 * @param {string} contentType - Content type to filter by
 * @returns {Promise<Array>} Array of content items
 */
export async function getPublishedContentByType(contentType) {
  const content = await loadContent();

  let items = [];
  switch (contentType) {
    case 'blog':
      items = content.blogs;
      break;
    case 'youtube':
      items = content.youtube;
      break;
    case 'external_link':
      items = content.externalLinks;
      break;
    default:
      return [];
  }

  return items
    .filter(item => item.status === 'published')
    .sort((a, b) => {
      const dateA = new Date(a.updated_at || a.created_at);
      const dateB = new Date(b.updated_at || b.created_at);
      return dateB - dateA;
    });
}

/**
 * Clear the content cache
 */
export function clearCache() {
  contentCache = null;
  isLoaded = false;
}

const contentService = {
  getPublishedContent,
  getContentByTypeAndId,
  getContentByUniqueId,
  getBlogById,
  getPublishedContentByType,
  clearCache
};

export default contentService;
